import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
} from "@headlessui/react";
import { XMarkIcon, UserCircleIcon, ChevronDownIcon, CheckIcon } from "@heroicons/react/24/outline";
import { Fragment } from "react";
import { Button } from "components/ui";
import { getUsersForAssignLeads } from "services/userService";
import { updateLeadOwner, bulkLeadActionDispatcher } from "services/leadsService";
import { toast } from "sonner";
import clsx from "clsx";
import { ConfirmModal } from "components/shared/ConfirmModal";
import { getUserIdFromToken } from "utils/tokenUtils";

const AssignOwnerModal = ({ isOpen, onClose, leadData, onOwnerChange, currentOwnerName, selectAllLeads = false, onSubmitOverride }) => {
  const [selectedOwner, setSelectedOwner] = useState(leadData?.assignedOwner || leadData?.assignedTo || "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [users, setUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const ownerButtonRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState({ left: 0, top: 0, width: 0 });
  const [showReassignConfirm, setShowReassignConfirm] = useState(false);

  const positionOwnerMenu = () => {
    if (!ownerButtonRef.current) return;
    const rect = ownerButtonRef.current.getBoundingClientRect();
    setMenuPosition({ left: rect.left, top: rect.bottom + 6, width: rect.width });
  };

  useEffect(() => {
    const handleRecalc = () => positionOwnerMenu();
    window.addEventListener('resize', handleRecalc, { passive: true });
    window.addEventListener('scroll', handleRecalc, { passive: true, capture: true });
    return () => {
      window.removeEventListener('resize', handleRecalc);
      window.removeEventListener('scroll', handleRecalc, { capture: true });
    };
  }, []);

  const saveRef = useRef(null);

  // Fetch users when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchUsers();
    }
  }, [isOpen]);

  const fetchUsers = async () => {
    setIsLoadingUsers(true);
    try {
      const response = await getUsersForAssignLeads();
      if (response.success) {
        setUsers(response.data || []);
        // Default to current user if none selected
        if (!selectedOwner) {
          const loggedInUserId = getUserIdFromToken();
          if (loggedInUserId) {
            setSelectedOwner(String(loggedInUserId));
          }
        }
      } else {
        toast.error("Failed to load users", {
          description: response.error || "Unable to fetch users for assignment"
        });
        setUsers([]);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Failed to load users");
      setUsers([]);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  // Filter users based on search query
  const filteredUsers = useMemo(() => {
    if (!searchQuery) return users;

    const lowerQuery = searchQuery.toLowerCase();
    return users.filter((user) =>
      user.fullName?.toLowerCase().includes(lowerQuery)
    );
  }, [users, searchQuery]);

  const handleSubmit = async () => {
    if (!selectedOwner) {
      toast.error("Please select an owner");
      return;
    }

    // If already assigned, show confirmation
    if (currentOwnerName && currentOwnerName !== "Not Assigned") {
      setShowReassignConfirm(true);
      return;
    }

    await performAssignment();
  };

  const performAssignment = async () => {
    setIsSubmitting(true);
    setShowReassignConfirm(false);
    try {
      if (onSubmitOverride) {
        await onSubmitOverride(selectedOwner);
        onClose();
        return;
      }

      let result;
      
      if (selectAllLeads) {
        // Use bulkLeadActionDispatcher when all leads are selected
        const bulkActionPayload = {
          // Get filters from parent component
          ...(leadData?.filters || {}),
          includeAllFiltered: true,
          
          // Action parameters
          actionType: 1, // Assign owner
          updateAssignedTo: selectedOwner,
        };

        result = await bulkLeadActionDispatcher(bulkActionPayload);
      } else {
        // Call the API to update lead owner for individual leads
        result = await updateLeadOwner(leadData?.id, selectedOwner);
      }

      if (result.success) {
        toast.success(result.message || "Lead owner assigned successfully");

        // Refresh lead data to get updated owner info
        if (onOwnerChange) {
          const newOwner = users.find((u) => u.userId === selectedOwner);
          await onOwnerChange({ 
            assignedTo: selectedOwner, 
            assignedOwner: newOwner?.fullName || "N/A" 
          });
        }

        onClose();
      } else {
        toast.error(result.error || "Failed to assign owner");
      }
    } catch (error) {
      console.error("Error assigning owner:", error);
      toast.error("Failed to assign owner");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-[99998] flex flex-col items-center justify-center overflow-hidden px-4 py-6 sm:px-5"
        onClose={() => { }}
        initialFocus={saveRef}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="absolute inset-0 bg-gray-900/50 backdrop-blur transition-opacity dark:bg-black/30" />
        </TransitionChild>

        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <DialogPanel className="relative flex w-full max-w-lg origin-top flex-col rounded-lg bg-white transition-all duration-300 dark:bg-dark-700 outline-none focus:outline-none">
            {/* Header - Fixed */}
            <div className="flex items-center justify-between rounded-t-lg bg-gray-200 px-4 py-3 dark:bg-dark-800 sm:px-5">
              <DialogTitle
                as="h3"
                className="flex items-center gap-2 text-base font-medium text-gray-800 dark:text-dark-100"
              >
                <UserCircleIcon className="h-5 w-5 text-blue-600" />
                Assign Owner
              </DialogTitle>
              <Button
                onClick={onClose}
                variant="flat"
                isIcon
                className="size-7 rounded-full ltr:-mr-1.5 rtl:-ml-1.5"
              >
                <XMarkIcon className="size-4.5" />
              </Button>
            </div>

            {/* Scrollable Content Area */}
            <div className="flex flex-col overflow-y-auto px-4 py-4 sm:px-5 max-h-[calc(85vh-140px)]">
              <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
                Assign {leadData?.name || "this lead"} to a team member for follow-up and management.
              </p>

              {/* Current Owner Display */}
              {currentOwnerName && (
                <div className="mb-4 rounded-lg bg-blue-50 px-4 py-3 dark:bg-blue-900/20">
                  <div className="flex items-center gap-2">
                    <UserCircleIcon className="size-5 text-blue-600 dark:text-blue-400" />
                    <div>
                      <p className="text-xs text-blue-600 dark:text-blue-400 font-medium">Current Owner</p>
                      <p className="text-sm font-semibold text-blue-900 dark:text-blue-100">{currentOwnerName}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-5">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {currentOwnerName ? "Reassign to" : "Lead Owner"} *
                  </label>

                  {isLoadingUsers ? (
                    <div className="flex items-center justify-center py-4">
                      <div className="size-5 animate-spin rounded-full border-2 border-primary-600 border-t-transparent" />
                      <span className="ml-2 text-sm text-gray-500">Loading users...</span>
                    </div>
                  ) : (
                    <Menu as="div" className="relative w-full">
                      {({ open }) => (
                        <>
                          <MenuButton
                            as={Button}
                            variant="outlined"
                            className="w-full justify-between"
                            disabled={isSubmitting}
                            ref={ownerButtonRef}
                            onClick={positionOwnerMenu}
                          >
                            <span className="truncate">
                              {selectedOwner ? (
                                users.find((u) => u.userId === selectedOwner)?.fullName || "Select Lead Owner"
                              ) : (
                                "Select Lead Owner"
                              )}
                            </span>
                            <ChevronDownIcon
                              className={clsx(
                                "size-4 transition-transform",
                                open && "rotate-180"
                              )}
                              aria-hidden="true"
                            />
                          </MenuButton>

                          <Transition
                            as={Fragment}
                            enter="transition ease-out duration-100"
                            enterFrom="opacity-0 translate-y-1"
                            enterTo="opacity-100 translate-y-0"
                            leave="transition ease-in duration-75"
                            leaveFrom="opacity-100 translate-y-0"
                            leaveTo="opacity-0 translate-y-1"
                          >
                            <MenuItems
                              portal
                              static
                              className="fixed z-[9999] max-h-[60vh] overflow-hidden rounded-lg border border-gray-300 bg-white shadow-lg shadow-gray-200/50 outline-none focus-visible:outline-none dark:border-dark-500 dark:bg-dark-700 dark:shadow-none"
                              style={{ left: `${menuPosition.left}px`, top: `${menuPosition.top}px`, width: `${menuPosition.width}px` }}
                            >
                              {/* Search Input (sticky) */}
                              <div className="sticky top-0 z-10 border-b border-gray-200 bg-white px-3 py-2 dark:border-dark-500 dark:bg-dark-700">
                                <input
                                  type="text"
                                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-500 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-dark-500 dark:bg-dark-600 dark:text-dark-100 dark:placeholder-gray-400"
                                  placeholder="Search users..."
                                  value={searchQuery}
                                  onChange={(e) => setSearchQuery(e.target.value)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </div>

                              {/* Scrollable user list */}
                              <div className="max-h-[150px] overflow-y-auto">
                                {filteredUsers.length === 0 ? (
                                  <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                                    {searchQuery ? "No users found" : "No users available"}
                                  </div>
                                ) : (
                                  filteredUsers.map((user) => (
                                    <MenuItem key={user.userId}>
                                      {({ focus }) => (
                                        <button
                                          onClick={() => setSelectedOwner(user.userId)}
                                          className={clsx(
                                            "flex h-9 w-full items-center px-3 tracking-wide outline-none transition-colors",
                                            focus &&
                                            "bg-gray-100 text-gray-800 dark:bg-dark-600 dark:text-dark-100",
                                            selectedOwner === user.userId &&
                                            "bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300"
                                          )}
                                        >
                                          <span className="flex-1 truncate text-left text-sm">
                                            {user.fullName}
                                          </span>
                                          {selectedOwner === user.userId && (
                                            <CheckIcon className="size-4 text-primary-600 dark:text-primary-400" />
                                          )}
                                        </button>
                                      )}
                                    </MenuItem>
                                  ))
                                )}
                              </div>
                            </MenuItems>
                          </Transition>
                        </>
                      )}
                    </Menu>
                  )}
                </div>
              </div>
            </div>

            {/* Footer Buttons - Fixed */}
            <div className="border-t border-gray-200 px-4 py-4 dark:border-dark-600 sm:px-5">
              <div className="flex justify-end space-x-3">
                <Button
                  onClick={onClose}
                  variant="outlined"
                  className="min-w-[7rem] rounded-full"
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmit}
                  color="primary"
                  ref={saveRef}
                  className="min-w-[7rem] rounded-full"
                  disabled={isSubmitting || !selectedOwner}
                >
                  {isSubmitting ? "Assigning..." : "Assign Owner"}
                </Button>
              </div>
            </div>
          </DialogPanel>
        </TransitionChild>
      </Dialog>
      
      {/* Reassign Confirmation Modal */}
      <ConfirmModal
        show={showReassignConfirm}
        state="pending"
        onClose={() => setShowReassignConfirm(false)}
        onOk={performAssignment}
        confirmLoading={isSubmitting}
        messages={{
          pending: {
            title: "Reassign Lead?",
            description: `This lead is already assigned to ${currentOwnerName}. Do you want to reassign it?`,
            actionText: "Yes, Reassign",
          }
        }}
        buttonColor="primary"
      />
    </Transition>
  );
};

export default AssignOwnerModal;
